import type { PodBrand } from '@/types/pod'

/**
 * The Cornwall Training & Consultancy pod — branding layer (browser side).
 *
 * A SALES DEMO, NOT A CUSTOMER. Cornwall Training & Consultancy Ltd (Unit 2
 * Southview House, St Austell Enterprise Park, PL25 4EJ) has not bought
 * anything and has not signed a DPA. This pod exists to be shown to their
 * team. Consequences, all deliberate, and identical in kind to swsteel:
 *   - registry entry carries NO `billing` flag and NO `customerSince`
 *     (`billing: 'operator'` marks the operator's OWN products, not a demo for
 *     someone else's business \u2014 swsteel's own config comment gets this wrong;
 *     its registry entry does not)
 *   - `notifyPhone` is the OPERATOR'S mobile — a captured enquiry here is a
 *     demo artefact, not their lead, and must not reach them
 *   - no `publishDomain`, no `missedCall` block: both point at a domain and a
 *     phone line the operator does not control
 * If they buy, all four flip and the ONBOARDING.md paperwork gate is the first
 * step, not the last.
 *
 * SOURCE OF TRUTH. Every value below is lifted from cornwalltraining.com, read
 * 15 Sep 2026: the office number, address and hours from the site header and
 * footer, the tagline from the hero, the starters from the questions their own
 * course index and FAQ show visitors actually arrive with. Nothing is
 * invented — the discipline the grounding fence enforces on the corpus applies
 * to the config too.
 *
 * WHAT THIS PITCH IS AGAINST. They run tawk.to on the homepage on the free
 * tier ("Powered by tawk.to" still shows, so no paid add-ons and no AI). On 15
 * Sep 2026 a live test asked it "what courses do you offer" during office
 * hours and a human replied with a link to the course index — a buying
 * question answered with the page the visitor was already on, and no capture.
 * Outside 08:30–17:00 Mon–Fri that box answers nothing at all. The assistant is
 * NOT positioned as a tawk replacement: it takes the repeat questions and the
 * out-of-hours capture, and hands anything real to the office.
 *
 * WELCOME INTRO LEADS ON COURSE SELECTION, DELIBERATELY. Their own published
 * FAQ answers "I'm unsure of what course I need?" with "call the office". That
 * is the single highest-volume question in this business and their website
 * cannot answer it. A greeting that opens with anything else wastes the demo.
 *
 * NO PRICES AND NO DATES IN THE GREETING OR THE STARTERS. Both live in their
 * WooCommerce booking system and move. See system-prompt.ts.
 *
 * PALETTE. Their site chrome: tangerine headings and CTA buttons on white,
 * with near-navy body copy. Taken from the live site so the assistant reads as
 * their own front desk. ESTIMATED FROM THE RENDERED PAGE, not from a brand
 * sheet — tune once seen live in the engine UI and beside their logo.
 *
 * CONTACT. Office landline and the admin inbox, both printed in the site
 * header. The email was held back on the first pass because Cloudflare
 * obfuscates it in the page source and a bouncing address in a demo is worse
 * than no address; it is carried now because it was read off the rendered
 * header on 15 Sep 2026.
 *
 * builtByForedesk IS ON, as on swsteel. This is a pitch asset shown to a
 * prospect, not a business's own front of house, so the credit is wanted. If
 * they buy, revisit it as the commercial decision the field's contract says it
 * is.
 */
export const ctcPod: PodBrand = {
  id: 'ctc',
  brandName: 'Cornwall Training',
  productName: 'Cornwall Training & Consultancy',
  assistantName: 'Merryn',
  tagline: 'Training, health & safety and occupational health across Cornwall',
  roleLabel: 'training assistant',
  welcomeIntro:
    "Not sure which course you need? That's the usual reason people start here. Tell me what it's for — the sort of work, whether it's one person or a team, and whether you're starting fresh or renewing a certificate — and I'll point you at the right course and get it to the office. First aid, FREC, health & safety, IOSH, construction, SIA, food safety, mental health and more, plus occupational health, fire risk assessments and face fit testing.",
  starters: [
    "I don't know which first aid course I need",
    'Can you train at our workplace?',
    'My certificate is about to run out',
    'Do you do face fit testing?',
  ],
  inputPrompt: 'Ask Merryn about courses and services',
  footerNote:
    "Merryn answers from Cornwall Training's own information — anything else goes straight to the office. Not first aid or medical advice; in an emergency call 999.",
  builtByForedesk: true,
  theme: {
    // Their site chrome: tangerine headings and buttons, near-navy text, white
    // surfaces. Sampled from the rendered homepage, 15 Sep 2026.
    '--color-brand-primary': '245 130 32', // tangerine #F58220
    '--color-brand-primary-hover': '214 110 22', // #D66E16
    '--color-brand-accent': '26 42 69', // navy #1A2A45
    '--color-surface-base': '250 250 249',
    '--color-surface-raised': '255 255 255',
    '--color-surface-subtle': '245 243 240',
    '--color-text-primary': '26 42 69',
    '--color-text-muted': '100 112 130',
    '--color-text-danger': '178 47 47',
    '--color-border-subtle': '230 228 224',
  },
  contact: {
    phone: '01726 932293',
    email: 'admin@cornwalltraining.com',
  },
}
